import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, PropertyValueMap, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
  PrintOptionDto,
  PrintOptionFilterDto,
  PrintOptionStepDto,
} from '@/api-sdk';

import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';
import { Mode } from '@/types';
import { isScrolledIntoView, scrollStepIntoView } from '@/utils';

import '../fg-configurator-step';
import './print-option/fg-print-option-filter-logo-option';
import './print-option/fg-print-option-filter-options-list';
import './print-option/fg-print-option-list';

@customElement('fg-configurator-step-print-option')
@localized()
export class FgConfiguratorStepPrintOption extends LitElement {
  @property({ type: Array })
  selectedOptions: PrintOptionStepDto['selectedOptions'] = [];

  @property({ type: String })
  cancelQueryString?: string | null;

  @property({ type: String })
  unprintedQueryString?: string | null;

  @property({ type: Boolean })
  unprinted?: boolean;

  @property({ type: Object })
  printOptionFilter?: PrintOptionFilterDto | null;

  @property({ type: String })
  addPrintOptionQueryString?: string | null;

  @property({ type: Boolean })
  showValidationMessage = false;

  @property({ type: String })
  configurationId?: string;

  @property({ type: Boolean })
  isLoading = false;

  @property({ type: String })
  validationMessage?: string | null;

  @property({ type: Boolean })
  scrollToStep = false;

  @property({ type: String })
  mode = Mode.DEFAULT;

  @state()
  showPrintOptionFilterOptions = true;

  @state()
  activePrintOptionCode = null;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  renderSkipPrintOptionStep() {
    return html`
      <div class="no-option-wrapper">
        <p>
          ${t('content.configurator.printOptionStep.noPrintOrPersonalization')}
        </p>

        <button
          class="button button-tertiary"
          @click=${this.togglePrintOptionStepSkipped}
        >
          ${t('actions.addPrintPosition')}
        </button>
      </div>
    `;
  }

  renderPrintOptionFilterOptions({
    filterOptions,
    filterQuestion,
    filterDescription,
    filterOptionLayout,
  }: PrintOptionFilterDto) {
    const showHeader = Boolean(
      filterQuestion || this.cancelQueryString !== null,
    );
    if (!filterOptions.length) {
      return nothing;
    }

    if (
      this.selectedOptions.length > 0 &&
      !this.showPrintOptionFilterOptions &&
      !this.isLoading
    ) {
      return html`
        <div class="extra-print-option-wrapper">
          <strong>
            ${t('headers.printPositionOptional', {
              positionIndex: this.selectedOptions.length + 1,
            })}
          </strong>
          <button
            class="button button-tertiary"
            @click=${() => this.setShowPrintOptionFilterOptions(true)}
          >
            ${t('actions.addPrintPosition')}
          </button>
        </div>
      `;
    }

    return html`
      <div class="filter-options-wrapper">
        ${!showHeader
          ? nothing
          : html`
              <div class="filter-options-header">
                ${filterQuestion &&
                html`
                  <strong>${filterQuestion}</strong>
                `}
                ${this.cancelQueryString !== null ||
                this.selectedOptions.length > 0
                  ? html`
                      <button
                        class="button-link"
                        @click=${() => this.cancelFilterOption()}
                      >
                        ${t('actions.cancel')}
                      </button>
                    `
                  : nothing}
              </div>
            `}
        ${!filterOptionLayout || !filterDescription
          ? nothing
          : html`
              <p>${filterDescription}</p>
            `}

        <fg-print-option-filter-options-list
          .filterOptions=${filterOptions}
          .filterOptionsLayout=${filterOptionLayout}
          @option-selected=${this.onOptionSelected}
        ></fg-print-option-filter-options-list>

        ${this.unprintedQueryString === null ||
        this.configuratorContext.mode === Mode.DIGITAL_EXAMPLE
          ? nothing
          : html`
              <button
                class="button-link"
                @click=${this.togglePrintOptionStepSkipped}
              >
                ${t('actions.noPrint')}
              </button>
            `}
      </div>
    `;
  }

  render() {
    if (!this.printOptionFilter && !this.unprinted) {
      return nothing;
    }

    return html`
      <fg-configurator-step
        .stepTitle=${t('headers.choosePrint')}
        .validationMessage=${this.validationMessage &&
        t(this.validationMessage)}
        .showValidationMessage=${this.showValidationMessage}
      >
        <div class="wrapper">
          <fg-print-option-list
            .options=${this.selectedOptions}
            .excludedPrintOptionCode=${this.activePrintOptionCode}
          ></fg-print-option-list>

          ${this.unprinted
            ? this.renderSkipPrintOptionStep()
            : this.printOptionFilter
              ? this.renderPrintOptionFilterOptions(this.printOptionFilter)
              : nothing}
        </div>
      </fg-configurator-step>
    `;
  }

  onOptionSelected(event: CustomEvent) {
    const { queryString } = event.detail.option;

    // checking the existence of 'printOptionFilter' in the queryString of a clicked filter option
    // to detect if this is the last clicked filter option before the actual print option is added.
    // If so, the filter options will be hidden until the 'add print option' button is clicked again.

    if (queryString.indexOf('printOptionFilter') === -1) {
      this.setShowPrintOptionFilterOptions(false);
    }
  }

  resetActivePrintOptionCode() {
    this.activePrintOptionCode = null;
  }

  togglePrintOptionStepSkipped() {
    let queryString = undefined;

    queryString = !this.unprinted
      ? this.unprintedQueryString
      : this.addPrintOptionQueryString;

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        detail: { option: { queryString: queryString } },
      }),
    );
  }

  setShowPrintOptionFilterOptions(show: boolean) {
    this.showPrintOptionFilterOptions = show;
  }

  cancelFilterOption() {
    if (this.selectedOptions.length > 0) {
      this.setShowPrintOptionFilterOptions(false);
    }

    if (this.cancelQueryString !== null) {
      this.dispatchEvent(
        new CustomEvent('option-selected', {
          composed: true,
          detail: {
            option: {
              queryString: this.cancelQueryString,
            },
          },
        }),
      );
    }
  }

  removePrintOption(
    queryString: PrintOptionDto['removePrintOptionQueryString'],
  ) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );

    this.resetActivePrintOptionCode();
  }

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (_changedProperties.has('scrollToStep') && this.scrollToStep) {
      scrollStepIntoView(this);
      isScrolledIntoView(this);
    }
  }

  connectedCallback(): void {
    super.connectedCallback();

    if (
      this.configuratorContext.configurationId &&
      this.selectedOptions.length > 0
    ) {
      this.setShowPrintOptionFilterOptions(false);
    }
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .wrapper {
        margin-top: 1rem;
      }

      .filter-options-wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .no-option-wrapper,
      .filter-options-header {
        display: flex;
        justify-content: space-between;
        gap: 1rem;
      }

      .no-option-wrapper {
        flex-direction: column;
      }

      .filter-options-header strong {
        font-size: 1rem;
      }

      .button-link {
        align-self: flex-start;
      }

      .no-option-wrapper p {
        font-size: 1rem;
        font-weight: 700;
      }

      .no-option-wrapper button {
        width: 100%;
      }

      .print-option-label {
        font-size: 1rem;
        display: block;
      }
      .extra-print-option-wrapper {
        display: flex;
        flex-direction: column;
      }

      .extra-print-option-wrapper strong {
        font-size: 1rem;
        display: block;
        margin-bottom: 0.625rem;
      }

      .extra-print-option-wrapper button {
        width: max-content;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .no-option-wrapper {
          flex-direction: row;
          gap: 0;
          align-items: center;
        }
        .no-option-wrapper button {
          width: auto;
        }
      }
    `,
  ];
}
